import { useCallback, useState } from "react";
import { offlineDb, type OfflineRapportAnnexe } from "@/core/offline/db";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ultra-mesure-token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Hook CRUD pour les annexes PDF d'un rapport.
 * Stockage Dexie + upload serveur immédiat si en ligne.
 */
export function useRapportAnnexes(rapportId: string | undefined) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const annexes =
    useLiveQuery(
      () =>
        rapportId
          ? offlineDb.rapportAnnexes
              .where("rapportId")
              .equals(rapportId)
              .toArray()
              .then((rows) => rows.sort((a, b) => a.order - b.order))
          : Promise.resolve([] as OfflineRapportAnnexe[]),
      [rapportId]
    ) ?? [];

  // ─── Ajouter une annexe (fichier PDF) ────────────────────
  // customSectionId : lier l'annexe à une section libre (sinon orpheline, fusion legacy en fin)
  // Retourne l'ID de l'annexe créée
  const addAnnexe = useCallback(
    async (file: File, customSectionId?: string): Promise<string> => {
      if (!rapportId) throw new Error("rapportId manquant");
      if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
        throw new Error("Le fichier doit être un PDF");
      }
      if (file.size > 50 * 1024 * 1024) {
        throw new Error("Fichier trop volumineux (max 50 Mo)");
      }

      const id = crypto.randomUUID();
      const now = new Date();
      const order = annexes.length;

      const local: OfflineRapportAnnexe = {
        id,
        rapportId,
        fileName: file.name,
        fileSize: file.size,
        order,
        customSectionId: customSectionId ?? null,
        blob: file,
        syncStatus: "pending",
        createdAt: now,
        updatedAt: now,
      };

      // 1. Stocker localement d'abord (la PDF est utilisable même hors ligne)
      await offlineDb.rapportAnnexes.add(local);

      // 2. Tenter l'upload serveur immédiat (si en ligne)
      if (navigator.onLine) {
        setUploading(true);
        setError(null);
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("annexeId", id);
          formData.append("rapportId", rapportId);
          formData.append("fileName", file.name);
          formData.append("order", String(order));
          if (customSectionId) formData.append("customSectionId", customSectionId);

          const res = await fetch(`/api/sync/annexes`, {
            method: "POST",
            headers: authHeaders(),
            body: formData,
          });
          if (!res.ok) throw new Error(`Erreur ${res.status}`);
          const { url } = await res.json();
          await offlineDb.rapportAnnexes.update(id, {
            serverUrl: url,
            syncStatus: "synced",
          });
        } catch (err) {
          // L'upload a échoué : on garde l'annexe en local, syncStatus reste pending
          setError(
            err instanceof Error
              ? `Annexe enregistrée localement (upload différé) : ${err.message}`
              : "Upload différé"
          );
        } finally {
          setUploading(false);
        }
      }
      return id;
    },
    [rapportId, annexes.length]
  );

  // ─── Supprimer une annexe ────────────────────────────────
  // Flow standard : DELETE serveur (404 toléré), pendingDeletion si offline,
  // suppression locale ensuite. La sync propage aux autres appareils via
  // le tombstone SyncDeletion créé côté serveur.
  const deleteAnnexe = useCallback(async (id: string) => {
    setError(null);
    await deleteWithOfflineFallback("annexe", id, `/api/sync/annexes/${id}`, () =>
      offlineDb.rapportAnnexes.delete(id).then(() => undefined)
    );
  }, []);

  // ─── Renommer une annexe ─────────────────────────────────
  // syncStatus pending : la prochaine sync pousse les métadonnées au serveur
  // (et le pull ne l'écrase pas tant qu'elle n'est pas repassée synced).
  const renameAnnexe = useCallback(async (id: string, fileName: string) => {
    await offlineDb.rapportAnnexes.update(id, {
      fileName,
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }, []);

  // ─── Réordonner ──────────────────────────────────────────
  const reorder = useCallback(
    async (idsInOrder: string[]) => {
      const now = new Date();
      await offlineDb.transaction("rw", offlineDb.rapportAnnexes, async () => {
        for (let i = 0; i < idsInOrder.length; i++) {
          await offlineDb.rapportAnnexes.update(idsInOrder[i], {
            order: i,
            updatedAt: now,
            syncStatus: "pending",
          });
        }
      });
    },
    []
  );

  return {
    annexes,
    uploading,
    error,
    addAnnexe,
    deleteAnnexe,
    renameAnnexe,
    reorder,
  };
}
